import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import {Button} from "primereact/button";
import BarcodeFields from "../../field/barcode/BarcodeFields";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import BarcodeHelper from "../../../../shared/helper/BarcodeHelper";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import BarcodeSwissQrCodeFormDialog from "./BarcodeSwissQrCodeFormDialog";
import BarcodeDialog, {BarcodeDialogProperties, BarcodeDialogStatus} from "./BarcodeDialog";
import {Dropdown} from "primereact/dropdown";
import {
	Barcode,
	Information,
	QrSwissPaymentBarcode,
	QrSwissPaymentContent,
	QrSwissPaymentContentFormat,
	QrSwissPaymentFormat,
	QrSwissPaymentSeparator,
	Settings
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import {PortalBarcode} from "../../../../shared/models/PortalBarcode";
import {PortalAddBarcode, PortalBarcodeFormat} from "../../../../shared/models/PortalAddBarcode";
import {SwissQrCodeForm} from "../../../../shared/models/SwissQrCodeForm";
import LengthUnit from "../../../../shared/units/LengthUnit";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import {Base64} from "js-base64";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";

interface BarcodeCreateDialogProperties extends BarcodeDialogProperties {
}

interface BarcodeCreateDialogStatus extends BarcodeDialogStatus {
}

export class BarcodeCreateDialogDefinition extends BarcodeDialog<BarcodeCreateDialogProperties,
	BarcodeCreateDialogStatus> {

	public static isEnabled(portalInfo: Information): boolean {
		return super.isEnabled(portalInfo);
	}

	public renderContents(): ReactNode {
		let parameter: PortalBarcode | undefined = this.state.parameter?.data;

		if (!FunctionHelper.exists(parameter?.add)) {
			return null;
		}

		let addParameter: PortalAddBarcode = parameter!.add as PortalAddBarcode;

		return (
			<>
				<div className="p-col-12 p-xl-8 p-grid divider-right-hidden">
					{this.renderOutputFormatOptions()}
					{this.renderContentOptions()}

					<fieldset className="p-col-12 p-grid">
						{this.renderFormatOptions()}
						{this.renderQrcodeOptions()}
						{this.renderAztecOptions()}
						{this.renderDatamatrixOptions()}
						{this.renderPdf417Options()}
						<div className="p-col-12 p-lg-6 p-grid" hidden={
							!this.state.fields?.contains(BarcodeFields.SWISSQRCODE_FORMAT) ||
							addParameter.format !== PortalBarcodeFormat.swissqrcode
						}>
							<label className="p-col-12 p-sm-5" htmlFor="control-swissqrcode-format">
								<Translate {...this.props} data="barcode.create.swissqrcode.format.label"
										   namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-7">
								<Dropdown inputId="control-swissqrcode-format"
										  value={FunctionHelper.applyValue(
											  addParameter.qrswiss![0]?.format, QrSwissPaymentBarcode.getFormatDefault()
										  )}
										  onChange={(e) => this.onChange(
											  BarcodeFields.SWISSQRCODE_FORMAT, e.value
										  )}
										  appendTo={document.getElementById("root")} options={[
									{
										label: Translate.value(
											"barcode.create.swissqrcode.format.codeonly", {ns: "dialog"}
										),
										value: QrSwissPaymentFormat.CodeOnly
									},
									{
										label: Translate.value(
											"barcode.create.swissqrcode.format.billonly", {ns: "dialog"}
										),
										value: QrSwissPaymentFormat.BillOnly
									},
									{
										label: Translate.value(
											"barcode.create.swissqrcode.format.billextraspace",
											{ns: "dialog"}
										),
										value: QrSwissPaymentFormat.BillExtraSpace
									},
									{
										label: Translate.value(
											"barcode.create.swissqrcode.format.billportraitsheet",
											{ns: "dialog"}
										),
										value: QrSwissPaymentFormat.BillPortraitSheet
									},
								]}/>
							</div>
						</div>
						<div className="p-col-12 p-lg-6 p-grid" hidden={
							!this.state.fields?.contains(BarcodeFields.SWISSQRCODE_SEPARATOR) ||
							addParameter.format !== PortalBarcodeFormat.swissqrcode ||
							addParameter.qrswiss![0]?.format === QrSwissPaymentFormat.CodeOnly
						}>
							<label className="p-col-12 p-sm-5" htmlFor="control-swissqrcode-separator">
								<Translate {...this.props} data="barcode.create.swissqrcode.separator.label"
										   namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-7">
								<Dropdown inputId="control-swissqrcode-separator"
										  value={FunctionHelper.applyValue(
											  addParameter.qrswiss![0]?.separator,
											  QrSwissPaymentBarcode.getSeparatorDefault()
										  )}
										  onChange={(e) => this.onChange(
											  BarcodeFields.SWISSQRCODE_SEPARATOR, e.value
										  )}
										  appendTo={document.getElementById("root")}
										  options={[
											  {
												  label: Translate.value(
													  "barcode.create.swissqrcode.separator.none",
													  {ns: "dialog"}
												  ),
												  value: QrSwissPaymentSeparator.None
											  },
											  {
												  label: Translate.value(
													  "barcode.create.swissqrcode.separator.solidline",
													  {ns: "dialog"}
												  ),
												  value: QrSwissPaymentSeparator.SolidLine
											  },
											  {
												  label: Translate.value(
													  "barcode.create.swissqrcode.separator.solidlinewithscissors",
													  {ns: "dialog"}
												  ),
												  value: QrSwissPaymentSeparator.SolidLineWithScissors
											  },
											  {
												  label: Translate.value(
													  "barcode.create.swissqrcode.separator.dottedline",
													  {ns: "dialog"}
												  ),
												  value: QrSwissPaymentSeparator.DottedLine
											  },
											  {
												  label: Translate.value(
													  "barcode.create.swissqrcode.separator.dottedlinewithscissors",
													  {ns: "dialog"}
												  ),
												  value: QrSwissPaymentSeparator.DottedLineWithScissors
											  },
											  {
												  label: Translate.value(
													  "barcode.create.swissqrcode.separator.dashedline",
													  {ns: "dialog"}
												  ),
												  value: QrSwissPaymentSeparator.DashedLine
											  },
											  {
												  label: Translate.value(
													  "barcode.create.swissqrcode.separator.dashedlinewithscissors",
													  {ns: "dialog"}
												  ),
												  value: QrSwissPaymentSeparator.DashedLineWithScissors
											  }
										  ]}/>
							</div>
						</div>
						{this.renderSwissQrCodeOptions()}
						<div className="p-col-12 p-lg-6 p-grid" hidden={
							!this.state.fields?.contains(BarcodeFields.UNIT)
						}>
							<label className="p-col-12 p-sm-5" htmlFor="control-unit">
								<Translate {...this.props} data="barcode.create.unit" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-7">
								<Dropdown inputId="control-unit"
										  value={FunctionHelper.applyValue(
											  addParameter.unit, PortalAddBarcode.getUnitDefault()
										  )}
										  options={[
											  {
												  label: Translate.value("inputs.unit.pt"),
												  value: LengthUnit.POINTS
											  },
											  {
												  label: Translate.value("inputs.unit.pc"),
												  value: LengthUnit.PICA
											  },
											  {
												  label: Translate.value("inputs.unit.mm"),
												  value: LengthUnit.MILLIMETER
											  },
											  {
												  label: Translate.value("inputs.unit.in"),
												  value: LengthUnit.INCHES
											  }
										  ]}
										  onChange={(e) => this.onChange(
											  BarcodeFields.UNIT, e.value
										  )}
										  appendTo={document.getElementById("root")}/>
							</div>
						</div>
						{this.renderGeneralOptions()}
					</fieldset>
				</div>

				{this.renderPreview()}
			</>
		);
	}

	public renderDialog(): ReactNode {
		let parameter: PortalBarcode = this.state.parameter!.data;
		let addParameter: PortalAddBarcode = parameter.add as PortalAddBarcode;

		let swisscodeFormat: QrSwissPaymentContentFormat = FunctionHelper.applyValue(
			addParameter.qrswiss![0]?.content?.format, QrSwissPaymentContent.getFormatDefault()
		);

		let classList: Array<string> = ["create-barcode-dialog", "verylarge"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<>
				<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
						appendTo={document.getElementById("root")} resizable={false}
						closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()}>
					<div className="p-grid">
						{this.renderError()}
						<Helper {...this.props}>
							<Translate {...this.props} data={"dialog.barcode.create.general"} namespace={"help"}
									   markdown={true}/>
						</Helper>
						{this.renderContents()}
					</div>
				</Dialog>
				<BarcodeSwissQrCodeFormDialog {...this.props} visible={this.state.showFormDialog}
											  parameter={this.state.parameter} format={swisscodeFormat}
											  submit={(value: SwissQrCodeForm) => {
												  this.onChange(
													  BarcodeFields.CONTENT, value.toSwissQrCode(
														  swisscodeFormat, true
													  )
												  );
											  }}
											  hide={() => this.setState({
												  showFormDialog: false
											  })}/>
			</>
		);
	}

	protected async onChange(field: BarcodeFields, value: any): Promise<void> {
		await super.onChange(field, value);

		let parameter: PortalBarcode = this.state.parameter!.data;
		let addParameter: PortalAddBarcode = parameter.add as PortalAddBarcode;
		let errors: Map<BarcodeFields, string> = this.state.fieldErrors;
		let hasError: boolean = false;

		switch (field) {
			case BarcodeFields.CONTENT:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
					hasError = true;
				}

				switch (addParameter.format) {
					case PortalBarcodeFormat.aztec:
						addParameter.aztec![0].value = value;
						break;
					case PortalBarcodeFormat.codabar:
						if (!BarcodeHelper.checkCodabarBoundary(value)) {
							errors.set(field, "errors.barcode.codabar_boundaries");
							hasError = true;
						} else if (!(await BarcodeHelper.isCodabar(value))) {
							errors.set(field, "errors.barcode.codabar");
							hasError = true;
						}

						addParameter.codabar![0].value = value;
						break;
					case PortalBarcodeFormat.code39:
						if (!(await BarcodeHelper.isCode39(value))) {
							errors.set(field, "errors.barcode.code39");
							hasError = true;
						}

						addParameter.code39![0].value = value;
						break;
					case PortalBarcodeFormat.code128:
						addParameter.code128![0].value = value;
						break;
					case PortalBarcodeFormat.datamatrix:
						addParameter.datamatrix![0].value = value;
						break;
					case PortalBarcodeFormat.ean8:
						if (!(await BarcodeHelper.isEan8(value))) {
							errors.set(field, "errors.barcode.ean8");
							hasError = true;
						}

						addParameter.ean8![0].value = value;
						break;
					case PortalBarcodeFormat.ean13:
						if (!(await BarcodeHelper.isEan13(value))) {
							errors.set(field, "errors.barcode.ean13");
							hasError = true;
						}

						addParameter.ean13![0].value = value;
						break;
					case PortalBarcodeFormat.itf:
						if (!(await BarcodeHelper.isItf(value))) {
							errors.set(field, "errors.barcode.itf");
							hasError = true;
						}

						addParameter.itf![0].value = value;
						break;
					case PortalBarcodeFormat.pdf417:
						addParameter.pdf417![0].value = value;
						break;
					case PortalBarcodeFormat.qrcode:
						addParameter.qrcode![0].value = value;
						break;
					case PortalBarcodeFormat.upca:
						if (!(await BarcodeHelper.isUpca(value))) {
							errors.set(field, "errors.barcode.upca");
							hasError = true;
						}

						addParameter.upca![0].value = value;
						break;
					case PortalBarcodeFormat.swissqrcode:
						addParameter.qrswiss![0].content!.value = value;
						break;
				}

				if (!hasError && errors.has(field)) {
					errors.delete(field);
				}
				break;
			case BarcodeFields.OUTPUT:
				addParameter.outputFormat = value;
				break;
			case BarcodeFields.UNIT:
				this.setState({
					unit: value
				});
				addParameter.unit = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter,
			previewBuild: false
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-barcode-create"}/>
					<Translate {...this.props} data="barcode.create.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected async submit(): Promise<void> {
		await super.submit();

		let operationData: PortalBarcode = this.cleanupOperationData();
		let addParameter: PortalAddBarcode = operationData.add as PortalAddBarcode;

		if ((operationData.add as PortalAddBarcode).format === PortalBarcodeFormat.swissqrcode) {
			addParameter.qrswiss![0].content!.value = Base64.toBase64(addParameter.getFormatContent());
		}

		let webServiceType: WebServiceType = WebServiceTypes.BARCODE;

		try {
			await this.props.context.session.executeRequest({
				webServiceType: webServiceType,
				operation: Barcode.fromJson(operationData.toJson()),
				filename: addParameter.fileName,
				settings: Settings.fromJson({
					pdfa: {
						pdfaEditProfile: this.props.pdfaEditProfile
					},
					signature: {
						signatureEditProfile: this.props.signatureEditProfile
					},
					compress: {
						compressProfile: this.props.compressProfile,
						compressObjects: {
							objectStreamSize: this.props.objectStreamSize
						}
					}
				} as Settings)
			});
		} catch (exception: any) {
			if (await this.onError(exception)) {
				await this.submit();
			}
			return;
		}

		let message: Message = new Message("success.barcode.create", MessageType.SUCCESS);
		this.props.context.getPage().addMessage(message);
		this.hide();
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let parameter: PortalBarcode = this.state.parameter!.data;

		if (!FunctionHelper.exists(parameter?.add)) {
			return;
		}

		let addParameter: PortalAddBarcode = parameter!.add as PortalAddBarcode;

		this.setState({
			parameter: this.state.parameter,
			defaultParameter: this.state.parameter?.clone(),
			unit: FunctionHelper.applyValue(addParameter.unit, PortalAddBarcode.getUnitDefault())
		});
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.BARCODE_CREATE;
	}
}

const BarcodeCreateDialog = React.memo(BarcodeCreateDialogDefinition,
	(prevProps: Readonly<BarcodeCreateDialogProperties>, nextProps: Readonly<BarcodeCreateDialogProperties>) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default BarcodeCreateDialog;